import React, { useEffect, useState } from 'react';
import Badge from '../Badge';
import { formatCurrency } from '../../../Utils/Formatters';
import { useCartContext } from '../../../Contexts/CartContext';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useTranslation } from 'react-i18next';
import { ORDER_TYPE_DELIVERY_ID } from '../../../Utils/Constants/Service';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import { useCartHook } from '../../../Hooks/CartHook';
import { RiCoinsFill } from 'react-icons/ri';

interface CartValueDetailsProps {
    cart: any;
    order?: any;
}

const CartValueDetails = ({ cart, order }: CartValueDetailsProps) => {

    const { t } = useTranslation();
    const { loadingUpdateQuantity, loadingCurrentCart } = useCartContext();
    const { company } = useLayoutContext();
    const { deleteFidelityDiscount } = useCartHook();
    const { isDelivery } = useOrderTypeContext();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(loadingUpdateQuantity || loadingCurrentCart);
    }, [loadingUpdateQuantity, loadingCurrentCart])

    if (!cart || cart.length === 0) return null;

    return (
        <div className='grid gap-0.5 text-sm'>

            <div className='flex justify-between'>
                <span>{t('cart.value_details.subtotal')}</span>
                <span className='flex items-center'>
                    {isLoading
                        ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                        : formatCurrency(cart.subtotal)
                    }
                </span>
            </div>

            {(order?.points_used > 0 || cart.points_used > 0 || cart.amount_points > 0) && (
                <div className='flex justify-between text-custom-primary font-medium'>
                    <span className='flex items-center gap-1.5'>
                        {t('cart.value_details.fidelity_points_used')}
                        {!order && (
                            <button onClick={() => deleteFidelityDiscount()}>
                                <Badge outline type="notify" className="rounded-full justify-center h-6 w-18 zoomInIconNoDelay text-xs capitalize">{t('checkout.coupon.button_remove_label')}</Badge>
                            </button>
                        )}
                    </span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : <span>{order?.points_used || cart.points_used || cart.amount_points} {t('layout.fidelity.points_short')}</span>
                        }
                    </span>
                </div>
            )}

            {cart.total_discount > 0 && (
                <div className='flex justify-between text-custom-primary font-medium'>
                    <span className='flex items-center gap-1'>
                        {t('cart.value_details.discounts')}
                        {cart.coupon_code && <Badge type='success' outline className='py-1 leading-3 font-bold md:text-xs'>{cart.coupon_code}</Badge>}
                    </span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : <>- { formatCurrency(cart.total_discount) }</>
                        }
                    </span>
                </div>
            )}

            {((!order && isDelivery) || (order?.type === ORDER_TYPE_DELIVERY_ID)) &&
                <div className='flex justify-between'>
                    <span>{t('cart.value_details.shipping_value')}</span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : cart.shipping_value > 0 ? formatCurrency(cart.shipping_value) : <Badge outline type='success' className='text-xs'>{t('cart.value_details.free')}</Badge>
                        }
                    </span>
                </div>
            }

            {cart.extra_tax > 0 &&
                <div className='flex justify-between'>
                    <span>{t('cart.value_details.service_fee')}</span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : formatCurrency(cart.extra_tax)
                        }
                    </span>
                </div>
            }

            {cart.extra_value > 0 &&
                <div className='flex justify-between'>
                    <span>{t('cart.value_details.extra_fee')}</span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : formatCurrency(cart.extra_value)
                        }
                    </span>
                </div>
            }

            {cart.value_points > 0 &&
                <div className='flex justify-between'>
                    <span className='flex items-center gap-0.5 text-xs font-medium text-custom-primary'>
                        <RiCoinsFill size={15} className='-ms-0.5' /> {company?.fidelity?.currency_name || t('cart.value_details.fidelity_discount')}
                    </span>
                    <span className='flex items-center'>
                        {isLoading
                            ? <div className="bg-gray-300 h-3 rounded-full w-12 animate-pulse"></div>
                            : <>- { formatCurrency(cart.value_points) }</>
                        }
                    </span>
                </div>
            }

            <div className='flex justify-between text-base font-extrabold text-shopfood-title'>
                <span>{t('cart.value_details.total')}</span>
                <span className='flex items-center'>
                {isLoading
                    ? <div className="bg-gray-400 h-4 rounded-full w-18 animate-pulse"></div>
                    : formatCurrency(Number(cart.total_with_discount) - Number(cart.value_points || 0))
                }
                </span>
            </div>

        </div>
    )
}

export default CartValueDetails;